import { useState } from 'react';
import { useRemovalRequests, useResolveRemoval } from '../../api/hooks';
import { removalReasonText } from '../../components/dialogs/RemovalDialog';
import { Icon } from '../../components/Icon';
import { RemovalReview } from '../../components/RemovalReview';
import { Who } from '../../components/Who';
import { fmtShortDate } from '../../lib/format';
import { useToast } from '../../state/toasts';

/** `/studio/removal-requests`: photos people asked to remove are hidden right away until someone in media decides. */
export function RemovalRequestsPage() {
  const list = useRemovalRequests();
  const resolve = useResolveRemoval();
  const toast = useToast();
  const open = (list.data ?? []).filter((r) => r.status === 'open');
  const [viewing, setViewing] = useState<number | null>(null);

  const act = (id: string, action: 'restore' | 'remove') =>
    resolve.mutate(
      { id, action },
      {
        onSuccess: () => {
          // Stay in the viewer on the next request (the resolved one drops out of the list), or close at the end.
          setViewing((v) => (v === null ? null : open.length <= 1 ? null : Math.min(v, open.length - 2)));
          toast(
            action === 'restore'
              ? { icon: 'check', title: 'Photo restored', sub: 'It’s visible in the library again.' }
              : { icon: 'trash', title: 'Photo removed', sub: 'It stays in Trash for 30 days.' },
          );
        },
        onError: (e) => toast({ icon: 'info', title: 'That didn’t work', sub: e.message }),
      },
    );

  const head = (
    <div className="toolbar">
      <h1>Removal requests</h1>
      <span className="meta">Photos are hidden as soon as someone asks for their removal.</span>
    </div>
  );
  if (list.isError)
    return (
      <>
        {head}
        <div className="empty">
          <h3>Couldn’t load removal requests</h3>
          {list.error.message}
        </div>
      </>
    );
  if (!list.data)
    return (
      <>
        {head}
        <div className="sentinel">
          <div className="spinner" />
        </div>
      </>
    );
  if (!open.length)
    return (
      <>
        {head}
        <div className="empty">
          <h3>All caught up</h3>No open removal requests.
        </div>
      </>
    );
  return (
    <>
      {head}
      <div className="tscroll">
        <table className="t">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Reason</th>
              <th>Requested by</th>
              <th>Date</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {open.map((r, i) => (
              <tr key={r.id}>
                <td>
                  {r.thumbUrl ? (
                    <button
                      type="button"
                      onClick={() => setViewing(i)}
                      title="Open photo"
                      aria-label="Open photo"
                      style={{ padding: 0, border: 0, background: 'none', cursor: 'zoom-in', display: 'block' }}
                    >
                      <img
                        src={r.thumbUrl}
                        alt=""
                        style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 6, display: 'block' }}
                      />
                    </button>
                  ) : (
                    <small style={{ color: 'var(--muted)' }}>{r.photoId}</small>
                  )}
                </td>
                <td style={{ maxWidth: 260 }}>
                  {removalReasonText(r)}
                  {r.note && r.reason !== 'other' && (
                    <>
                      <br />
                      <small style={{ color: 'var(--muted)' }}>{r.note}</small>
                    </>
                  )}
                </td>
                <td>
                  <Who name={r.requesterName} />
                </td>
                <td style={{ color: 'var(--muted)' }}>{fmtShortDate(new Date(r.createdAt))}</td>
                <td>
                  <div className="rowbtns">
                    <button type="button" className="btn sm" onClick={() => setViewing(i)}>
                      <Icon name="eye" />
                      View
                    </button>
                    <button type="button" className="btn sm" disabled={resolve.isPending} onClick={() => act(r.id, 'restore')}>
                      Restore photo
                    </button>
                    <button type="button" className="btn sm danger" disabled={resolve.isPending} onClick={() => act(r.id, 'remove')}>
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="help" style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 10 }}>
        Click a photo to see it full size. Restore puts the photo back; Remove keeps it in Trash.
      </p>
      {viewing !== null && (
        <RemovalReview
          requests={open}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onResolve={act}
          busy={resolve.isPending}
        />
      )}
    </>
  );
}
